/* Modified by CheckoutWorks.dev */

/**
 * Storefront Inspector.
 *
 * Reads the public storefront files every Shopify store serves
 * (/meta.json, /products.json, /collections.json) from the visitor's own
 * browser and reports what they reveal. Nothing is sent to CheckoutWorks,
 * stored, or exported.
 *
 * The first part is pure logic with no DOM or network access, so Node tests
 * can import it. The <cw-storefront-inspector> element at the end only
 * registers in a browser.
 */

export const MAX_PRODUCT_PAGES = 4;
export const PRODUCTS_PER_PAGE = 250;
export const REQUEST_DELAY_MS = 400;
export const REQUEST_TIMEOUT_MS = 10000;
export const MAX_EXAMPLES = 5;

const CJK = /[぀-ヿ㐀-鿿가-힯]/;
const ALIEXPRESS_ITEM_ID = /\b1005\d{9,}\b/;
const ALIEXPRESS_SKU_ATTR = /\b\d{1,6}:\d{3,}(#|;|$)/;
const SUPPLIER_WORDS = /\b(aliexpress|alibaba|1688|taobao|dsers|cjdropshipping|zendrop|spocket|wholesale|factory|trading|co\.,?\s*ltd|ltd\.?|official store)\b/i;
const SUPPLIER_CONTACT = /(alicdn\.com|aliexpress\.|1688\.com|taobao\.com|wechat|whatsapp|\+86[\s\d-]{6,})/i;
const INTERNAL_TAG = /^(test|testing|draft|dsers|aliexpress|ali|cj|supplier|cost|margin|hidden|internal|todo|copy|wholesale|imported?)([-_: ].*)?$/i;
const TEST_TITLE = /\b(test|testing|copy of|do not (buy|purchase|order)|placeholder|untitled|dummy)\b/i;
const SUPPLIER_IMAGE_NAME = /^(?:[SH][0-9a-f]{32}|[0-9a-f]{32}|1005\d{9,})/i;

/**
 * Turns whatever the visitor typed into a store origin.
 * Returns null for input that cannot be a public web address.
 */
export function normalizeStoreUrl(input) {
  let value = String(input || '').trim();
  if (!value) return null;
  if (!/^https?:\/\//i.test(value)) value = `https://${value}`;
  let url;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  const host = url.hostname.toLowerCase();
  if (!host.includes('.') || host.endsWith('.local') || /^[\d.]+$/.test(host) || host === 'localhost') return null;
  return `https://${host}`;
}

function sameDay(iso) {
  return typeof iso === 'string' ? iso.slice(0, 10) : '';
}

function fileName(src) {
  try {
    const path = new URL(src, 'https://cdn.shopify.com').pathname;
    return decodeURIComponent(path.split('/').pop() || '');
  } catch {
    return '';
  }
}

function pushExample(list, value) {
  if (value && list.length < MAX_EXAMPLES && !list.includes(value)) list.push(value);
}

function toNumber(value) {
  const number = Number.parseFloat(value);
  return Number.isFinite(number) ? number : null;
}

/**
 * Builds the report from the three public files.
 * `meta` may be null; `products` and `collections` are arrays.
 */
export function analyzeStore({ meta = null, products = [], collections = [], productsTruncated = false } = {}) {
  const store = meta
    ? {
        name: meta.name || '',
        domain: meta.domain || '',
        myshopifyDomain: meta.myshopify_domain || '',
        location: [meta.city, meta.province, meta.country].filter(Boolean).join(', '),
        currency: meta.currency || '',
        publishedProducts: meta.published_products_count ?? null,
        publishedCollections: meta.published_collections_count ?? null,
      }
    : null;

  const findings = [];
  const add = (finding) => findings.push({ examples: [], ...finding });

  // Public by design: explain what anyone can read, even when nothing is wrong.
  if (store?.myshopifyDomain) {
    add({
      id: 'myshopify-domain',
      level: 'info',
      title: "This store's myshopify.com address is public",
      detail: 'Every Shopify store exposes its original myshopify.com address, even with a custom domain. It confirms the store runs on Shopify.',
      fix: 'If this is your store: this cannot be hidden. Keep in mind that the address can show an old or test name.',
      examples: [store.myshopifyDomain],
    });
  }

  if (store?.location) {
    add({
      id: 'store-location',
      level: 'info',
      title: "The store's city and region are public",
      detail: "The city, region, and country from the store's address appear in a public file.",
      fix: 'If this is your store and it shows a home address you would rather keep private, review the store address in Settings > General. Use an address you are allowed to use for the business.',
      examples: [store.location],
    });
  }

  if (products.length) {
    add({
      id: 'catalog-public',
      level: 'info',
      title: 'The full published catalog is readable as data',
      detail: `Prices, compare-at prices, variants, SKUs, tags, vendors, and creation dates for ${products.length}${productsTruncated ? '+' : ''} products are available in one public file. This is how the storefront works, not a security issue.`,
      fix: 'If this is your store: this cannot be turned off in Shopify. Treat everything published to the Online Store as public.',
    });
  }

  // Worth reviewing: details that often leak supplier or internal information.
  const vendorCounts = new Map();
  const vendorIssues = [];
  const tagIssues = [];
  const skuIssues = [];
  const imageIssues = [];
  const descriptionIssues = [];
  const testProducts = [];
  const compareAtIssues = [];
  const createdDays = new Map();

  for (const product of products) {
    const title = product.title || product.handle || 'Untitled product';
    const vendor = product.vendor ?? '';
    vendorCounts.set(vendor, (vendorCounts.get(vendor) || 0) + 1);
    if (vendor && (vendor !== vendor.trim() || CJK.test(vendor) || SUPPLIER_WORDS.test(vendor))) {
      pushExample(vendorIssues, `"${vendor}" on ${title}`);
    }

    const tags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',');
    for (const tag of tags.map((t) => String(t).trim()).filter(Boolean)) {
      if (INTERNAL_TAG.test(tag) || SUPPLIER_WORDS.test(tag)) pushExample(tagIssues, `"${tag}" on ${title}`);
    }

    const variants = Array.isArray(product.variants) ? product.variants : [];
    for (const variant of variants) {
      const sku = String(variant.sku || '');
      if (sku && (ALIEXPRESS_ITEM_ID.test(sku) || ALIEXPRESS_SKU_ATTR.test(sku) || SUPPLIER_WORDS.test(sku))) {
        pushExample(skuIssues, `${sku} on ${title}`);
      }
      const price = toNumber(variant.price);
      const compareAt = toNumber(variant.compare_at_price);
      if (price !== null && compareAt !== null && compareAt > 0 && compareAt <= price) {
        pushExample(compareAtIssues, `${title}: price ${variant.price}, compare-at ${variant.compare_at_price}`);
      }
    }

    // Free gifts and samples are often priced at 0, so only the title marks a test product.
    if (TEST_TITLE.test(title)) pushExample(testProducts, title);

    for (const image of Array.isArray(product.images) ? product.images : []) {
      const name = fileName(image.src || '');
      if (name && (SUPPLIER_IMAGE_NAME.test(name) || CJK.test(name) || SUPPLIER_WORDS.test(name))) {
        pushExample(imageIssues, `${name} on ${title}`);
      }
    }

    const body = String(product.body_html || '');
    const bodyMatch = body.match(SUPPLIER_CONTACT) || (CJK.test(body) ? ['Chinese, Japanese, or Korean text'] : null);
    if (bodyMatch) pushExample(descriptionIssues, `${title}: ${bodyMatch[0]}`);

    const day = sameDay(product.created_at);
    if (day) createdDays.set(day, (createdDays.get(day) || 0) + 1);
  }

  if (vendorIssues.length) {
    add({
      id: 'vendor',
      level: 'review',
      title: 'Vendor names that may reveal a supplier or look unfinished',
      detail: 'The vendor field is public. Supplier store names, non-English text, or stray spaces tell shoppers and competitors where products come from, or look careless.',
      fix: 'If this is your store, set the vendor to your own brand name in Products, or with a bulk edit.',
      examples: vendorIssues,
    });
  }

  if (tagIssues.length) {
    add({
      id: 'tags',
      level: 'review',
      title: 'Tags that look internal',
      detail: 'Product tags are public, including tags you only use for automation or organization.',
      fix: 'If this is your store, rename or remove internal tags, or keep that information in a metafield instead.',
      examples: tagIssues,
    });
  }

  if (skuIssues.length) {
    add({
      id: 'sku',
      level: 'review',
      title: 'SKUs that may contain supplier item IDs',
      detail: 'SKUs are public. Some look like marketplace item numbers or option codes that point back to a supplier listing.',
      fix: 'If this is your store, replace them with your own SKU scheme. If a dropshipping app uses the SKU to match orders, check its settings before you change them.',
      examples: skuIssues,
    });
  }

  if (imageIssues.length) {
    add({
      id: 'image-names',
      level: 'review',
      title: 'Image file names that may point to a supplier',
      detail: 'Image URLs keep the original file name. Hash-style names from marketplaces, item numbers, or non-English names can be traced back to a supplier listing.',
      fix: 'If this is your store, rename files to short descriptive names before uploading, or replace the images. Descriptive names also help image search.',
      examples: imageIssues,
    });
  }

  if (descriptionIssues.length) {
    add({
      id: 'descriptions',
      level: 'review',
      title: 'Descriptions with supplier links or untranslated text',
      detail: 'Product descriptions are public in full, including images hosted on a supplier site, contact details, or text left in another language.',
      fix: 'If this is your store, edit the description, move images into Files, and remove supplier links and contact details.',
      examples: descriptionIssues,
    });
  }

  if (testProducts.length) {
    add({
      id: 'test-products',
      level: 'review',
      title: 'Products that look like tests or placeholders',
      detail: 'These products are published to the Online Store, so they appear in the public catalog even if no collection shows them.',
      fix: 'If this is your store, set them to Draft, or remove the Online Store sales channel from them.',
      examples: testProducts,
    });
  }

  if (compareAtIssues.length) {
    add({
      id: 'compare-at',
      level: 'review',
      title: 'Compare-at prices that are not higher than the price',
      detail: 'A compare-at price at or below the selling price shows no discount and can make a sale look broken.',
      fix: 'If this is your store, raise or clear the compare-at price on these variants.',
      examples: compareAtIssues,
    });
  }

  if (products.length >= 5) {
    const [topDay, topCount] = [...createdDays.entries()].sort((a, b) => b[1] - a[1])[0] || ['', 0];
    if (topCount / products.length >= 0.8) {
      add({
        id: 'created-dates',
        level: 'info',
        title: 'Most products share one creation date',
        detail: `${topCount} of ${products.length} products were created on ${topDay}. This usually means a bulk import or a re-import, so creation dates say little about how long a product has been selling. Reading them to judge which products sell leads to the wrong conclusion.`,
        fix: 'Nothing to fix. Deleting and re-importing products resets their creation dates.',
      });
    }
  }

  const levels = { review: 0, info: 1 };
  findings.sort((a, b) => levels[a.level] - levels[b.level]);

  return {
    store,
    productCount: products.length,
    productsTruncated,
    collectionCount: collections.length,
    vendors: [...vendorCounts.entries()].sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count })),
    findings,
  };
}

/* ---------------------------------------------------------------------- */
/* Browser                                                                 */
/* ---------------------------------------------------------------------- */

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function fetchJson(url) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(url, { signal: controller.signal, credentials: 'omit', headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** Reads the public files in sequence, with a pause between requests. */
export async function readStore(origin, onProgress = () => {}) {
  onProgress('Reading store details');
  const meta = await fetchJson(`${origin}/meta.json`);
  if (!meta) return null;

  const products = [];
  let productsTruncated = false;
  for (let page = 1; page <= MAX_PRODUCT_PAGES; page += 1) {
    await sleep(REQUEST_DELAY_MS);
    onProgress(`Reading products (page ${page})`);
    const data = await fetchJson(`${origin}/products.json?limit=${PRODUCTS_PER_PAGE}&page=${page}`);
    const batch = Array.isArray(data?.products) ? data.products : [];
    products.push(...batch);
    if (batch.length < PRODUCTS_PER_PAGE) break;
    if (page === MAX_PRODUCT_PAGES) productsTruncated = true;
  }

  await sleep(REQUEST_DELAY_MS);
  onProgress('Reading collections');
  const collectionData = await fetchJson(`${origin}/collections.json?limit=250`);
  const collections = Array.isArray(collectionData?.collections) ? collectionData.collections : [];

  return { meta, products, collections, productsTruncated };
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

if (typeof HTMLElement !== 'undefined' && typeof customElements !== 'undefined') {
  class StorefrontInspector extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="store"]');
      this.status = this.querySelector('.cw-sfi__status');
      this.results = this.querySelector('.cw-sfi__results');
      this.button = this.querySelector('button[type="submit"]');
      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.run();
      });
    }

    setStatus(text, isError = false) {
      this.status.textContent = text;
      this.status.classList.toggle('cw-sfi__status--error', isError);
    }

    async run() {
      const origin = normalizeStoreUrl(this.input.value);
      this.results.replaceChildren();
      if (!origin) {
        this.setStatus('Enter a store address, such as your-store.com.', true);
        return;
      }
      this.button.disabled = true;
      this.setAttribute('aria-busy', 'true');
      try {
        const data = await readStore(origin, (step) => this.setStatus(`${step}…`));
        if (!data) {
          this.setStatus(
            "We couldn't read this store's public files. It may not run on Shopify, may be password-protected, or may block these files.",
            true,
          );
          return;
        }
        const report = analyzeStore(data);
        this.render(origin, report);
        const reviewCount = report.findings.filter((f) => f.level === 'review').length;
        this.setStatus(reviewCount ? `Done. ${reviewCount} item${reviewCount === 1 ? '' : 's'} worth reviewing.` : 'Done. Nothing flagged for review.');
      } finally {
        this.button.disabled = false;
        this.removeAttribute('aria-busy');
      }
    }

    render(origin, report) {
      const fragment = document.createDocumentFragment();

      const summary = el('div', 'cw-sfi__summary');
      const rows = [
        ['Store', report.store?.name || origin.replace('https://', '')],
        ['myshopify.com address', report.store?.myshopifyDomain || '—'],
        ['Location', report.store?.location || '—'],
        ['Currency', report.store?.currency || '—'],
        ['Products read', `${report.productCount}${report.productsTruncated ? '+ (first 1,000 only)' : ''}`],
        ['Collections read', String(report.collectionCount)],
      ];
      const list = el('dl', 'cw-sfi__facts');
      for (const [label, value] of rows) {
        list.append(el('dt', '', label), el('dd', '', value));
      }
      summary.append(el('h3', 'cw-sfi__summary-title', 'What we could read'), list);
      fragment.append(summary);

      if (!report.findings.some((f) => f.level === 'review')) {
        fragment.append(el('p', 'cw-sfi__clean', 'No vendor names, tags, SKUs, file names, descriptions, test products, or compare-at prices were flagged in the products we read.'));
      }

      for (const finding of report.findings) {
        const card = el('article', `cw-sfi__finding cw-sfi__finding--${finding.level}`);
        const badge = el('span', 'cw-sfi__badge', finding.level === 'review' ? 'Worth reviewing' : 'Public by design');
        card.append(badge, el('h3', 'cw-sfi__finding-title', finding.title), el('p', 'cw-sfi__detail', finding.detail));
        if (finding.examples.length) {
          const examples = el('ul', 'cw-sfi__examples');
          for (const example of finding.examples) examples.append(el('li', '', example));
          card.append(examples);
        }
        const fix = el('p', 'cw-sfi__fix');
        fix.append(el('strong', '', 'What to do: '), document.createTextNode(finding.fix));
        card.append(fix);
        fragment.append(card);
      }

      this.results.replaceChildren(fragment);
    }
  }

  if (!customElements.get('cw-storefront-inspector')) {
    customElements.define('cw-storefront-inspector', StorefrontInspector);
  }
}
